import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Hero from '../components/home/Hero';
import UploadSection from '../components/home/UploadSection';
import FAQSection from '../components/home/FAQSection';
import ProcessingModal from '../components/home/ProcessingModal';
import { submitProject } from '../services/api';

const Home = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);

  const processingSteps = [
    "Scanning script for key details...",
    "Matching against global incentive database...",
    "Preparing your instant matches..."
  ];

  const handleSubmit = async (formData) => {
    setIsProcessing(true);
    setProcessingStep(0);

    try {
      // Simulate processing steps
      for (let i = 0; i < processingSteps.length; i++) {
        setProcessingStep(i);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const result = await submitProject(formData);
      
      if (result.success) {
        navigate(`/results/${result.submissionId}`);
      } else {
        throw new Error(result.error || 'Failed to process submission');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to process your submission. Please try again.');
      setIsProcessing(false);
    }
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How is this free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We want to provide helpful services for users and offer other subscription-based services. Our goal is to help filmmakers access global incentives that might otherwise be difficult to find."
        }
      },
      {
        "@type": "Question",
        "name": "Is our data private?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We use third-party AI services to analyze and generate content for this service. Your project data is processed securely and we follow strict privacy guidelines."
        }
      },
      {
        "@type": "Question",
        "name": "What files can I upload?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can upload PDF or TXT files up to 20MB. We support most script formats and project descriptions for comprehensive analysis."
        }
      },
      {
        "@type": "Question",
        "name": "How accurate are the estimates?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Estimates are based on current incentive rates and typical local spend requirements. Always verify details directly with the relevant film commission for the most accurate information."
        }
      },
      {
        "@type": "Question",
        "name": "Do you store my files?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We temporarily process your files for analysis but do not permanently store them. Any data retention is minimal and follows industry privacy standards."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>NuPitch Incentive Match Maker - Film Tax Credits & Rebates</title>
        <meta name="description" content="Upload your script or paste your film idea. Get instant matches to global tax credits and rebates in seconds. Free, no account needed." />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdFaq)}
        </script>
      </Helmet>

      <div className="min-h-screen">
        <Hero />
        
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="section-spacing bg-white"
        >
          <div className="container-responsive">
            <UploadSection onSubmit={handleSubmit} />
          </div>
        </motion.section>

        <FAQSection />

        <ProcessingModal
          isOpen={isProcessing}
          currentStep={processingStep}
          steps={processingSteps}
        />
      </div>
    </>
  );
};

export default Home;